import React, { useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import {
  ownerPercentagesValid,
  validateCorpName,
  validateCorpTag,
} from '@astra/shared';
import {
  PALETTE,
  Card,
  CardLabel,
  CardRow,
  CardValue,
  ErrText,
  Hint,
  SectionTitle,
  inputStyle,
  primaryBtnStyle,
  smallBtnStyle,
} from '../station/stationTheme';
import { localCorpUsername } from './useCorporationData';
import type { OwnerRow } from './types';

interface Props {
  /** Local player's current credit balance — used to validate the founder's own contribution. */
  credits: number;
  busy: boolean;
  /** Server-side or validation error to display. */
  error: string | null;
  /** Setter so the form can surface its own client-side validation errors. */
  setError: (msg: string | null) => void;
  /** Called when the user submits a valid creation contract. */
  onSubmit: (payload: {
    name: string;
    tag: string;
    managerName: string;
    totalCapital: number;
    owners: { username: string; percentage: number }[];
  }) => void;
}

export function CreateCorpForm({ credits, busy, error, setError, onSubmit }: Props) {
  const me = localCorpUsername();
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [capital, setCapital] = useState(0);
  const [owners, setOwners] = useState<OwnerRow[]>([{ username: me, percentage: 100 }]);
  const [manager, setManager] = useState(me);

  const updateOwner = (i: number, patch: Partial<OwnerRow>) =>
    setOwners((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const addOwner = () => setOwners((prev) => [...prev, { username: '', percentage: 0 }]);
  const removeOwner = (i: number) => setOwners((prev) => prev.filter((_, idx) => idx !== i));

  const submit = () => {
    setError(null);
    const nameErr = validateCorpName(name);
    if (nameErr) { setError(nameErr); return; }
    const tagErr = validateCorpTag(tag);
    if (tagErr) { setError(tagErr); return; }
    if (!ownerPercentagesValid(owners)) { setError('Owner percentages must sum to exactly 100.'); return; }
    if (!owners.some((o) => o.username === manager)) { setError('Manager must be one of the owners.'); return; }
    if (!owners.some((o) => o.username === me)) { setError('You (the founder) must be an owner.'); return; }
    const myContribution = Math.floor((capital * (owners.find((o) => o.username === me)?.percentage ?? 0)) / 100);
    if (myContribution > credits) { setError('You cannot afford your own contribution.'); return; }

    onSubmit({
      name: name.trim(),
      tag,
      managerName: manager,
      totalCapital: Math.floor(capital),
      owners: owners.map((o) => ({ username: o.username.trim(), percentage: o.percentage })),
    });
  };

  const sum = owners.reduce((a, o) => a + (Number(o.percentage) || 0), 0);

  return (
    <>
      <Card>
        <CardRow>
          <CardLabel>Name</CardLabel>
          <CardValue>
            <input
              style={inputStyle}
              value={name}
              maxLength={32}
              onChange={(e) => setName(e.target.value)}
              placeholder="Stellar Mining Co."
            />
          </CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Tag (1–4)</CardLabel>
          <CardValue>
            <input
              style={{ ...inputStyle, width: 70 }}
              value={tag}
              maxLength={4}
              onChange={(e) => setTag(e.target.value.toUpperCase())}
              placeholder="SMC"
            />
          </CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Initial capital</CardLabel>
          <CardValue>
            <input
              style={{ ...inputStyle, width: 120 }}
              type="number"
              min={0}
              value={capital}
              onChange={(e) => setCapital(Math.max(0, parseInt(e.target.value) || 0))}
            />
          </CardValue>
        </CardRow>
        <Hint>Capital is split among owners by percentage and debited only when every owner accepts.</Hint>
      </Card>

      <Card>
        <SectionTitle>Owners — must sum to 100% (now {sum}%)</SectionTitle>
        {owners.map((o, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={o.username}
              placeholder="pilot name"
              onChange={(e) => updateOwner(i, { username: e.target.value })}
            />
            <input
              style={{ ...inputStyle, width: 64 }}
              type="number"
              min={1}
              max={100}
              value={o.percentage}
              onChange={(e) => updateOwner(i, { percentage: Math.max(0, parseInt(e.target.value) || 0) })}
            />
            <span style={{ color: PALETTE.DIM, fontSize: 11 }}>%</span>
            {owners.length > 1 && (
              <button style={smallBtnStyle(true)} onClick={() => removeOwner(i)}>
                <FiTrash2 size={11} />
              </button>
            )}
          </div>
        ))}
        <button style={smallBtnStyle(false)} onClick={addOwner}>
          <FiPlus size={11} /> Add owner
        </button>
      </Card>

      <Card>
        <CardRow>
          <CardLabel>Manager</CardLabel>
          <CardValue>
            <select style={{ ...inputStyle }} value={manager} onChange={(e) => setManager(e.target.value)}>
              {owners.map((o, i) => (
                <option key={i} value={o.username}>{o.username || '(unnamed)'}</option>
              ))}
            </select>
          </CardValue>
        </CardRow>
        <Hint>The manager is the super-user. A pilot can manage at most one corporation.</Hint>
      </Card>

      <button disabled={busy} style={primaryBtnStyle(busy)} onClick={submit}>
        {busy ? 'Sending…' : 'SEND CREATION CONTRACT'}
      </button>
      {error && <ErrText>{error}</ErrText>}
    </>
  );
}
